<!-- AIChatFloat.vue -->
<template>
  <div class="chat-float-container">
    <!-- Floating button -->
    <button
      class="chat-toggle-btn"
      :class="{ active: isOpen }"
      @click="toggleChat"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        ></path>
      </svg>
    </button>

    <!-- Chat panel -->
    <transition name="slide">
      <div v-if="isOpen" class="chat-panel" @click.stop>
        <!-- Header -->
        <div class="chat-header">
          <div class="ai-info">
            <div class="ai-avatar">QS</div>
            <div>
              <div class="ai-name">AI Assistant</div>
              <div class="ai-status">Online</div>
            </div>
          </div>
          <button class="close-btn" @click="toggleChat">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 6L6 18"></path>
              <path d="M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Messages area -->
        <div class="chat-messages" ref="messages">
          <div
            v-for="msg in messages"
            :key="msg.id"
            :class="['message', msg.role]"
          >
            <div class="message-bubble">
              <div
                v-if="msg.role === 'assistant'"
                v-html="renderMarkdown(msg.content)"
                class="markdown-body inh"
              ></div>
              <div class="inh" v-else>
                {{ msg.content }}
              </div>
              <span :class="['msg-time', msg.role]">
                {{ formatDatetime(msg.datetime) }}
              </span>
              <div v-if="msg.articles && msg.articles.length">
                <div v-for="(article, index) in msg.articles" :key="index">
                  <a
                    :href="article.url"
                    :target="newsWidgetActive ? '_self' : '_blank'"
                    rel="noopener noreferrer"
                    class="news-link"
                    @click.prevent="onArticleClick(article)"
                  >
                    Source {{ index + 1 }}: {{ article.source }} ↗
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="isTyping" class="message assistant">
            <div class="message-bubble typing">
              <QSLoading
                :isLoading="isTyping"
                :size="30"
                :colorTheme="colorTheme"
              />
              {{ typingDots }}
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="chat-input">
          <input
            ref="chatInput"
            v-model="userInput"
            @keyup.enter="sendMessage"
            placeholder="Type a message..."
            :disabled="isTyping"
          />
          <button
            @click="sendMessage"
            :disabled="!userInput.trim() || isTyping"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
        <div class="chat-disclaimer">
          Quanspire does not use client data with AI agents
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import QSLoading from "@/components/QSLoading.vue";
import MarkdownIt from "markdown-it";

const md = new MarkdownIt({
  breaks: true,
  linkify: true,
});

export default {
  name: "AIChatFloat",
  components: { QSLoading },
  data() {
    return {
      typingDots: ".",
      typingInterval: null,
      isOpen: false,
      userInput: "",
      isTyping: false,
    };
  },
  props: {
    colorTheme: { required: true, type: Boolean, default: true },
  },
  computed: {
    messages() {
      const stored = this.$store.getters["user_preferences/aiChatMessages"];
      if (stored.length === 0) {
        return [this.defaultMessage()];
      }
      return stored.map((msg) => ({
        ...msg,
        datetime: new Date(msg.datetime),
      }));
    },
    newsWidgetActive() {
      const isOverview = this.$route.path.startsWith("/overview");
      if (!isOverview) return false;
      const layout =
        this.$store.getters["user_preferences/overviewChoices"]?.currentLayout;
      return (
        Array.isArray(layout) &&
        layout.some((slot) => slot?.componentId === "newsWidget")
      );
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this.loadHistory();
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    isTyping(newVal) {
      if (!newVal && this.isOpen) {
        this.$nextTick(() => {
          this.$refs.chatInput?.focus();
        });
      }
    },
  },
  methods: {
    defaultMessage() {
      return {
        id: 1,
        datetime: new Date(),
        role: "assistant",
        content: "Hi! I am your AI assistant. How can I help you?",
        metadata: {
          topic: "new topic",
          intent: "None",
          language: "Undisclosed",
          tags: [],
        },
      };
    },
    persistMessages(messages) {
      this.$store.dispatch(
        "user_preferences/updateAiChatMessages",
        messages.map((msg) => ({
          ...msg,
          datetime: msg.datetime instanceof Date ? msg.datetime.toISOString() : msg.datetime,
        })),
      );
    },
    renderMarkdown(content) {
      return md.render(content || "");
    },
    formatDatetime(date) {
      const d = new Date(date);
      const now = new Date();

      const isSameDay =
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();

      if (isSameDay) {
        // just hours and minutes: "14:30"
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }

      // full date: "May 05, 26, 14:30"
      return d.toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    startTypingAnimation() {
      const states = [
        "",
        ".",
        "..",
        "...",
        "... Fetching related data",
        "... Analyzing data",
        "... Gathering insights",
      ];
      let index = 0;

      this.typingDots = states[index];

      this.typingInterval = setInterval(() => {
        index = (index + 1) % states.length;
        this.typingDots = states[index];
      }, 1500); // speed
    },
    stopTypingAnimation() {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
    },
    toggleChat() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          // Focus en el input
          this.$refs.chatInput?.focus();

          // Scroll to the end
          const messages = this.$refs.messages;
          if (messages) {
            messages.scrollTop = messages.scrollHeight;
          }
        });
      }
    },

    async sendMessage() {
      if (!this.userInput.trim() || this.isTyping) return;

      const userMsg = this.userInput.trim();
      this.userInput = "";

      // Add user message
      const updatedMessages = [
        ...this.messages,
        {
          id: Date.now(),
          datetime: new Date(),
          role: "user",
          content: userMsg,
        },
      ];
      this.persistMessages(updatedMessages);
      this.isTyping = true;
      this.startTypingAnimation();
      this.$nextTick(this.scrollToBottom);

      try {
        // ----> AI ENDPOINT <----
        const conversation_history = this.messages
          .slice(0, -1) // remove last message
          .slice(-10) // keep last 10 previous messages
          .map((msg) => ({
            role: msg.role,
            content:
              msg.role === "assistant"
                ? JSON.stringify({
                    topic: msg.metadata?.topic,
                    tags: msg.metadata?.tags ?? [],
                  })
                : msg.content,
            datetime: msg.datetime
              .toISOString()
              .slice(0, 16)
              .replace("T", " "),
          }));
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.VUE_APP_GATEWAY_URL}chat/message`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify({
              user_prompt: userMsg,
              conversation_history: conversation_history,
            }),
          },
        );
        const data = await response.json();
        const aiReply =
          data.response || "Sorry, I could not process your request.";
        const nArticles = data?.news_count || 0;
        const urlArticles = data?.articles || 0;
        const metadata = data?.metadata || 0;
        // Simulate typing delay
        setTimeout(() => {
          this.persistMessages([
            ...this.messages,
            {
              id: Date.now() + 1,
              datetime: new Date(),
              role: "assistant",
              content: aiReply,
              metadata: metadata,
              nArticles: nArticles,
              articles: urlArticles,
            },
          ]);
          this.isTyping = false;
          this.stopTypingAnimation();
          this.$nextTick(this.scrollToBottom);
        }, 800);
      } catch (err) {
        this.persistMessages([
          ...this.messages,
          {
            id: Date.now() + 1,
            datetime: new Date(),
            role: "assistant",
            content: "Connection error. Please try again.",
          },
        ]);
        this.isTyping = false;
        this.stopTypingAnimation();
        this.$nextTick(this.scrollToBottom);
      }
    },

    async loadHistory() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await fetch(
          `${process.env.VUE_APP_GATEWAY_URL}chat/conversation`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!data.messages || data.messages.length === 0) return;
        const mapped = data.messages.map((msg) => ({
          id: msg.id,
          datetime: msg.created_at,
          role: msg.role,
          content: msg.content,
          metadata: msg.metadata || null,
          articles: msg.sources || [],
          nArticles: (msg.sources || []).length,
        }));
        this.persistMessages(mapped);
        this.$nextTick(this.scrollToBottom);
      } catch (err) {
        console.warn("[Chat] Could not load history:", err);
      }
    },

    scrollToBottom() {
      const container = this.$refs.messages;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    onArticleClick(article) {
      if (this.newsWidgetActive) {
        this.$root.$emit("qs:open-news", article.url);
        this.isOpen = false;
      } else {
        window.open(article.url, "_blank", "noopener,noreferrer");
      }
    },
    handleClickOutside(e) {
      if (
        this.isOpen &&
        !e.target.closest(".chat-panel") &&
        !e.target.closest(".chat-toggle-btn")
      ) {
        this.isOpen = false;
      }
    },
  },
};
</script>

<style scoped></style>
